import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Modal, Form, Button, Message, Label, Icon } from 'semantic-ui-react'
import { DatePicker } from '../shared/DatePicker'
import WeekDaysSelect from '../shared/WeekDaysSelect'
import PropTypes from "prop-types";
import { onSuccess, onError, onWarning } from '../shared/messages'
import HotelsDropDown from '../shared/HotelsDropDown';
import RatesDropDown from '../shared/RatesDropDown';
import RoomTypesDropDown from '../shared/RoomTypesDropDown';
import ProductsDropDown from '../shared/ProductsDropDown';
import { dateFormat, serverDateFormat, EntityType, RatePricingTypesEnum } from '../shared/constants';
import _ from 'lodash'
import { useTextResources } from '../../language';
import moment from 'moment';
import NumericInput from '../shared/NumericInput';
import { isBrowser, isMobileOnly } from 'mobile-device-detect'
import { getIntWeekdays, toServerDateFormat } from '../shared/utils';
import ValidateModalPermission from '../shared/ValidateModalPermission';
import { PermissionsActions } from '../shared/permissionsActions';
import AllowExternalUpdatesDropDown from '../shared/AllowExternalUpdatesDropDown';
import { Live } from '../../api/services/next';
import { RateCategory } from '../shared/constants'
import './style.css'
import { useLoader } from '../../hooks/useLoader';

const PriceUpdatePopup = ({ onClose, onSave, HotelID, controller, startOpen, initialDate, open: openProp, ...rest }) => {
    const text = useTextResources()
    const loader = useLoader()
    const today = moment().format(serverDateFormat);
    const [dates, setDates] = useState(() => initialDate ? [toServerDateFormat(initialDate), toServerDateFormat(initialDate)] : [today, today]);
    const [open, setOpen] = useState(startOpen)
    const isOpen = open || openProp;
    const [days, setDays] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const [hotelLevel, setHotelLevel] = useState(false)
    const [roomTypes, setRoomTypes] = useState([])
    const [rate, setRate] = useState(null)
    const [product, setProduct] = useState(null)
    const [rule, setRule] = useState(null)
    const [value, setValue] = useState("0")
    const [valueSingle, setValueSingle] = useState("0")
    const [valueDouble, setValueDouble] = useState("0")
    const [valuePerRoom, setValuePerRoom] = useState("0")
    const [params, setParams] = useState(null)
    const [rateItem, setRateItem] = useState(null)
    const [hotelId, setHotelId] = useState(HotelID)
    const [selectedDates, setSelectedDates] = useState([])
    const [allowExternalUpdate, setAllowExternalUpdate] = useState();
    const [selectedHotel, setSelectedHotel] = useState(HotelID);

    const handleDatesChanged = (newValue) => {
        if (newValue) {
            setDates(newValue);
        } else {
            setDates(initialDate ? [toServerDateFormat(initialDate), toServerDateFormat(initialDate)] : [today, today]);
        }
    }

    useEffect(() => {
        if (controller) {
            window[controller] = (action, params) => {
                switch (action) {
                    case "open":
                        if (params) {
                            setDates([params.StartDate || dates[0], params.EndDate || dates[1]])
                            setRate(params.RateID)
                            setRoomTypes(params.RoomTypeIds || [])
                            setProduct(params.ProductID ? { ID: params.ProductID } : null)
                            setHotelId(params.HotelId || HotelID)
                            setParams(params)
                            setAllowExternalUpdate(params.AllowExternalUpdate)
                        } else {
                            setRate(null)
                            setRoomTypes([])
                            setProduct(null)
                            setAllowExternalUpdate(null)
                            setParams(null)
                        }
                        setRule(null)
                        setValue("0")
                        setValueSingle("0")
                        setValueDouble("0")
                        setValuePerRoom("0")
                        setHotelLevel(false)
                        setOpen(true)
                        setRateItem(null)
                        setSelectedDates([])
                        break;
                    default:
                        break;
                }
            }
        }
    }, [])

    useEffect(() => {
        setProduct(null);
    }, [rateItem])

    useEffect(() => {
        setValueSingle("0")
        setValueDouble("0")
        setValuePerRoom("0")
    }, [rateItem, product, roomTypes, hotelLevel, rule])

    useEffect(() => {
        if (selectedHotel) {
            setHotelId(selectedHotel)
        }
    }, [selectedHotel]);

    const ratePricingTypeId = useMemo(() => {
        return rateItem?.RatePricingElements.some(rpe => rpe.EntityTypeId === EntityType.roomTypes && rpe.RatePricingTypeId === RatePricingTypesEnum.PerPerson) ?
            RatePricingTypesEnum.PerPerson :
            rateItem?.RatePricingElements[0]?.RatePricingTypeId;
    }, [rateItem]);

    const checkIfValid = () => {
        const missingFields = []

        if (!selectedHotel) {
            missingFields.push(text("Hotel"))
        }

        if (missingFields.length === 0) {
            return true;
        }

        onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
        return false;
    }

    const saveAction = async (close) => {
        if (!checkIfValid()) return

        setIsSaving(true)
        loader(true)
        let data = {}
        data[rule] = Math.abs(Number(value))
        switch (rule) {
            case "Percent":
                data["LiveTypeOfChangeOfPrice"] = 1;
                break;
            case "Amount":
                data["LiveTypeOfChangeOfPrice"] = 2;
                break;
            case "Fixed":
                data["LiveTypeOfChangeOfPrice"] = 3;
                break;
        }
        try {
            const alldates = selectedDates.length ? selectedDates : [{
                startDate: dates[0],
                endDate: dates[1]
            }]
            const updated = await Promise.all(alldates.map(date => {
                return Live.UpdatePrice({
                    FromDate: toServerDateFormat(date.startDate),
                    ToDate: toServerDateFormat(date.endDate),
                    WeekDays: getIntWeekdays(days),
                    RoomTypeIds: roomTypes,
                    RateIds: rate ? [rate] : null,
                    ProductIds: product ? [product.Id] : null,
                    HotelId: hotelId,
                    AllowExternalUpdate: allowExternalUpdate,
                    PerRoomPrice: valuePerRoom,
                    PerPersonSinglePrice: valueSingle,
                    PerPersonDoublePrice: valueDouble,
                    PercentOrAmount: value,
                    OverridePriceType: data.LiveTypeOfChangeOfPrice
                })
            }))
            if (onSave) {
                onSave(updated)
            }
            if (params && params.OnSave) {
                params.OnSave(updated)
            }
            onSuccess()
            if (close) {
                closeAction();
            }
        } catch (error) {
            console.error(error)
            error.status === 400 ? onWarning(error) : onError(error)
        }
        finally {
            loader(false)
            setIsSaving(false)
        }
    }

    const closeAction = () => {
        setOpen(false);
        resetFields();
        onClose?.();
    }

    useEffect(() => {
        if (!isOpen) {
            resetFields();
        }
    }, [isOpen]);

    // MonthlySideMenu opens this popup declaratively via the `open`/`initialDate`
    // props (no `controller`); dates was only ever seeded once at mount, so
    // picking a different day cell before opening the popup left it showing
    // whatever date was current the first time the component mounted.
    useEffect(() => {
        if (openProp) {
            setDates(initialDate ? [toServerDateFormat(initialDate), toServerDateFormat(initialDate)] : [today, today]);
        }
    }, [openProp]);

    const RoomTypesSpec = useMemo(() => {
        let spec = {
            hotelId
        }
        if (rate) {
            spec.RateIDs = rate
        }
        return spec
    }, [hotelId, rate])

    const RatesSpec = useMemo(() => {
        let spec = {
            hotelId,
            IncludeBaseRates: true,
            IncludeDayUse: false,
            IncludeRatePricingType: true,
            IsActive: true,
            InheritedRates: false,
            FromDate: toServerDateFormat(dates[0]),
            ToDate: toServerDateFormat(dates[1]),
            ExcludeRateCategoryIDs: [RateCategory.ExtraAddOn],
            includes: ["RatePricingElements"]
        }
        if (!_.isEmpty(roomTypes)) {
            spec.RoomTypeIds = roomTypes
        }
        return spec
    }, [hotelId, roomTypes, dates])

    const ProductsSpec = useMemo(() => {
        let spec = {
            hotelId,

            StartDate: toServerDateFormat(dates[0]),
            EndDate: toServerDateFormat(dates[1]),
            IgnoreRestrictions: true,
            SkipChildren: true,
            SkipCache: false
        }
        if (!_.isEmpty(roomTypes)) {
            spec.RoomTypeIds = roomTypes
        }
        if (rate) {
            spec.RateIds = rate
        }
        return spec
    }, [hotelId, roomTypes, rate, dates])

    const texts = {
        rule: rule && text(rule),
        SingleFixedPrice: text("SingleFixedPrice"),
        DoubleFixedPrice: text("DoubleFixedPrice"),
        ByPercent: text("ByPercent"),
        ByAmount: text("ByAmount"),
        BasePrice: text("BasePrice"),
        SetFixedPrice: text("SetFixedPrice"),
        RoomFixedPrice: text("RoomFixedPrice")
    }

    const onChangeAllowExternalUpdate = (value) => {
        if (value === "" || value === undefined) {
            value = null;
        }

        setAllowExternalUpdate(value);
    }

    const priceOptions = () => {
        if ((rateItem || !_.isEmpty(product)) && !hotelLevel) {
            return [
                {
                    text: texts.ByPercent,
                    value: "Percent",
                },
                {
                    text: texts.ByAmount,
                    value: "Amount",
                },
                {
                    text: texts.SetFixedPrice,
                    value: "Fixed",
                }
            ]
        } else {
            return [
                {
                    text: texts.ByPercent,
                    value: "Percent",
                }
            ]
        }
    }

    const isSaveEnabled = useCallback(() => {
        return !isSaving && (hotelLevel || !_.isEmpty(roomTypes) || rate || product) && (rule || allowExternalUpdate !== undefined)
    }, [isSaving, rule, hotelLevel, roomTypes, rate, product, allowExternalUpdate])

    const addDateToList = () => {
        if (selectedDates.some((d) => moment(d.startDate).isSame(dates[0], 'day') && moment(d.endDate).isSame(dates[1], 'day'))) {
            onWarning(text('WarningDuplicateDates'))
            return
        }
        setSelectedDates([
            ...selectedDates,
            {
                startDate: dates[0],
                endDate: dates[1]
            }
        ])
    }

    const onRemoveDateFromSelectedDates = async (index, e) => {
        if (e) {
            await setSelectedDates(selectedDates => selectedDates.filter((_, i) => i !== index))
        }
    }

    const resetFields = () => {
        setSelectedDates([])
        setDates(initialDate ? [toServerDateFormat(initialDate), toServerDateFormat(initialDate)] : [today, today])
        setHotelLevel(false)
        setRoomTypes([])
        setRate(null)
        setProduct(null)
        setRule(null)
        setAllowExternalUpdate(null)
        setValue("0")
        setValueSingle("0")
        setValueDouble("0")
        setValuePerRoom("0")
        setSelectedHotel(HotelID)
    }

    const memoDates = useMemo(() => {
        return selectedDates.length ? selectedDates : [{ startDate: dates[0], endDate: dates[1] }]
    }, [selectedDates, dates])

    const priceFields = () => {
        if (rule) {
            if (rule === "Fixed") {
                const getFields = () => {
                    const pricingTypeId = ratePricingTypeId || product?.RatePricingTypeId
                    const isVilla = rateItem?.IsVilla || product?.IsVilla
                    if (pricingTypeId === 1) {
                        return <Form.Field width={4} label={texts["RoomFixedPrice"]} buttonPlacement="none" control={NumericInput} min={0} max={9999999} onChange={setValuePerRoom} value={valuePerRoom} />
                    } else if (pricingTypeId === 2) {
                        if (isVilla) {
                            return <Form.Field width={4} button label={texts["BasePrice"]} buttonPlacement="none" control={NumericInput} min={0} max={9999999} onChange={setValueDouble} value={valueDouble} />
                        } else {
                            return <>
                                <Form.Field width={4} label={texts["SingleFixedPrice"]} buttonPlacement="none" control={NumericInput} min={0} max={9999999} onChange={setValueSingle} value={valueSingle} />
                                <Form.Field width={4} label={texts["DoubleFixedPrice"]} buttonPlacement="none" control={NumericInput} min={0} max={9999999} onChange={setValueDouble} value={valueDouble} />
                            </>
                        }
                    }
                }
                return getFields()
            } else {
                return <Form.Field width={4} label={texts.rule} buttonPlacement="none" control={NumericInput} min={rule === "Percent" ? -100 : -9999999} max={rule === "Percent" ? 100 : 9999999} digits={rule === "Percent" ? 0 : 2} onChange={setValue} value={value} />
            }
        }
        return null
    }

    return (
        <ValidateModalPermission open={isOpen} onClose={onClose} actionId={PermissionsActions.AccessPricesPopup}>
            <Modal size={isMobileOnly ? "fullscreen" : "tiny"} className={isMobileOnly ? "mobile" : ""} open={isOpen} style={{ zIndex: 1000000 }} {...rest} >
                <Modal.Header>{text("PricePopup")}</Modal.Header>
                <Modal.Content style={isMobileOnly ? { overflowY: "auto" } : { maxHeight: 600, overflowY: "auto" }}>
                    <Form size="small">
                        <Form.Group unstackable widths={'equal'} style={{ flexWrap: "wrap" }}>
                            {selectedDates.map((selectedDate, index) => (
                                <Label.Group key={index}>
                                    <Label as="a">
                                        {moment(selectedDate.startDate).format(dateFormat)}
                                        -
                                        {moment(selectedDate.endDate).format(dateFormat)}
                                        <Icon key={index} name={"delete"} onClick={onRemoveDateFromSelectedDates.bind(this, index)} />
                                    </Label>
                                </Label.Group>
                            ))}
                        </Form.Group>
                        <Form.Group unstackable style={{ alignItems: 'flex-end' }}>
                            <Form.Field width={12} style={{ display: 'grid' }}>
                                <DatePicker picktype='range' onChange={handleDatesChanged} label={text("DateRange")} value={dates} clearable={false} />
                            </Form.Field>
                            <Form.Field width={4} className='button-field' ><Button primary onClick={addDateToList}>{text("AddDate")}</Button></Form.Field>
                        </Form.Group>
                        <Form.Field label={text("Weekdays")} control={WeekDaysSelect} onChange={setDays} dates={memoDates} />
                        <Form.Checkbox label={text("HotelLevel")} onChange={(e, { checked }) => {
                            setHotelLevel(checked);
                            setRoomTypes([]);
                            setRate(null)
                            setProduct(null);
                            setRule(null);
                        }} checked={hotelLevel} />
                        <Form.Field
                            label={text("Hotel")}
                            required
                            control={HotelsDropDown}
                            onChange={setSelectedHotel}
                            value={selectedHotel}
                        />
                        {
                            !hotelLevel && <React.Fragment>
                                <Form.Group widths={2} unstackable>
                                    <Form.Field label={text("RoomType")} clearable multiple search control={RoomTypesDropDown} spec={RoomTypesSpec} onChange={setRoomTypes} value={roomTypes} placeholder={text("All")} />
                                    <Form.Field label={text("Rate")} clearable search control={RatesDropDown} spec={RatesSpec} onChange={setRate} onChangeItem={setRateItem} value={rate} />
                                </Form.Group>
                                {!window.SelfieSettings &&
                                    <Form.Field
                                        label={text("Product")}
                                        clearable
                                        search
                                        control={ProductsDropDown}
                                        descriptionField="RateName"
                                        spec={ProductsSpec}
                                        onChangeItem={setProduct}
                                        textField="Name"
                                        value={product ? product.Id : null} />
                                }
                            </React.Fragment>
                        }
                        <Message warning size="small" visible>
                            {text("PriceOverridingAffects")}
                        </Message>
                        <Form.Group>
                            <Form.Select width={6} clearable label={text("PriceRule")} options={priceOptions()} onChange={(e, { value }) => setRule(value)} value={rule} />
                            {priceFields()}
                        </Form.Group>
                        <Form.Field
                            label={text("UpdateFromExternalChannel")}
                            control={AllowExternalUpdatesDropDown}
                            onChange={onChangeAllowExternalUpdate}
                            value={allowExternalUpdate}
                            clearable
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    {isBrowser && <Button disabled={isSaving} negativesize={isMobileOnly ? "tiny" : "small"} negative onClick={closeAction}>{text("Close")}</Button>}
                    {isBrowser && <Button size={"small"} onClick={resetFields}>{text("Reset")}</Button>}
                    {isBrowser && <Button disabled={!isSaveEnabled()} loading={isSaving} positive size={isMobileOnly ? "tiny" : "small"} onClick={() => saveAction(true)}>{text("Saveandclose")}</Button>}
                    <Button disabled={!isSaveEnabled()} loading={isSaving} positive size={isMobileOnly ? "tiny" : "small"} onClick={() => saveAction(false)}>{text("Save")}</Button>
                </Modal.Actions>
            </Modal>
        </ValidateModalPermission>
    )
}

PriceUpdatePopup.propTypes = {
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    HotelID: PropTypes.any,
    controller: PropTypes.string,
    startOpen: PropTypes.bool,
    initialDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    open: PropTypes.bool
}


export default PriceUpdatePopup
